import React, { useState } from 'react'
import papa from 'papaparse'
import { RouteComponentProps } from 'react-router-dom'
import {
  useStateWithDebounce,
  useAuth,
  UserPermissions,
  useInsuranceCompanies,
  InsuranceCompanies_getInsuranceCompanies_insuranceCompanies,
  useGetPatientsNotApprovedInsuranceCompanies,
  GetPatientNotApprovedInsuranceCompanies,
} from '@modmd/data'
import { useSortChangeHandler } from 'utils/helpers'
import DashboardPage from 'internal-portal/sharedComponents/DashboardPage'
import { Box, Container } from 'components/Layout'
import Icon from 'components/Icon'
import TextInput from 'components/TextInput'
import { useSearchState } from 'internal-portal/utils/useSearchState'
import Button from 'components/Button'
import { DEVICE } from 'theme'
import { useIsMinDevice } from 'utils/hooks/useMedia'
import { SelectInput } from 'components/SelectInput'
import { InsuranceCompaniesDatalist } from 'sharedComponents/InsuranceCompaniesDatalist'
import { Downloading } from 'components/Downloading'
import { APPROVED_OPTIONS } from './utils'
import { CreateCompanyDialog } from './CreateCompanyDialog'

type EventsProps = RouteComponentProps

type FilterState = {
  isApproved?: string
  name?: string
  page?: string
}

const PAGE_LENGTH = 10

const InsuranceCompanies: React.VFC<EventsProps> = () => {
  const { hasPermission } = useAuth()
  const isMobile = !useIsMinDevice(DEVICE.TABLET)
  const searchState = useSearchState<FilterState>()
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [
    editInsuranceCompanyState,
    setEditInsuranceCompanyState,
  ] = useState<InsuranceCompanies_getInsuranceCompanies_insuranceCompanies>()

  const [
    getPatients,
    { loading: loadingPatientsNotApprovedInsurance },
  ] = useGetPatientsNotApprovedInsuranceCompanies({
    onCompleted: (data: GetPatientNotApprovedInsuranceCompanies) => {
      const csvData = data.getPatientsNotApprovedInsurance.map((patient) => ({
        'last name': patient?.lastName ?? '',
        'first name': patient?.firstName ?? '',
        email: patient?.email ?? '',
        'insurance company': patient?.Insurance?.insuranceCompany ?? '',
      }))

      const csv = papa.unparse(csvData, { header: true })
      const dataBlob = new Blob([csv], { type: 'text/csv' })

      const link = document.createElement('a')
      link.href = window.URL.createObjectURL(dataBlob)
      link.setAttribute('download', `Patients_Not_Approved_Insurances.csv`)
      document.body.appendChild(link)
      link.click()
    },
  })

  const downloadCSV = () => {
    getPatients()
  }

  const { sorter, handleSortChange } = useSortChangeHandler()

  const { data: insuranceCompanies, loading: isCompaniesLoading, refetch } = useInsuranceCompanies({
    filter: {
      isApproved: searchState.data.isApproved,
      name: searchState.data.name,
    },
    page: Number(searchState.data.page) || 1,
    pageLength: PAGE_LENGTH,
  })

  const [filterData, setFilterData] = useStateWithDebounce<FilterState>({}, (filter) => {
    searchState.setSearchState({ ...filter, page: 1 }, false, true)
  })

  const handleInputChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { value, name } = event.target
      setFilterData({ ...filterData, [name]: value })
    },
    [filterData, setFilterData]
  )

  return (
    <DashboardPage
      Header={
        <>
          <Box display="flex" justifyContent="flex-end" p="1rem">
            {hasPermission([UserPermissions.SEE_EDIT_INSURANCE_COMPANIES]) && (
              <>
                <Button
                  onClick={downloadCSV}
                  leftIcon={<Icon.CloudDownloadOutline size="L" />}
                  marginRight="0.25rem"
                >
                  {loadingPatientsNotApprovedInsurance ? (
                    <Downloading />
                  ) : (
                    ' Patients with Not Approved Insurances'
                  )}
                </Button>
                <Button
                  onClick={() => {
                    setIsCreateOpen(true)
                  }}
                  leftIcon={<Icon.Plus size="L" />}
                  marginRight="0.25rem"
                >
                  New Insurance Company
                </Button>
              </>
            )}
          </Box>
        </>
      }
    >
      <Container>
        <InsuranceCompaniesDatalist
          insuranceCompanies={insuranceCompanies?.getInsuranceCompanies?.insuranceCompanies || []}
          pagination={insuranceCompanies?.getInsuranceCompanies?.pagination}
          isLoading={isCompaniesLoading}
          tableHeader={
            <Box
              display="grid"
              gridGap="1rem"
              gridTemplateColumns={
                isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(13rem, max-content))'
              }
            >
              <TextInput
                name="name"
                label="Insurance Company name"
                value={filterData.name || ''}
                onChange={handleInputChange}
                leftIcon={<Icon.Search />}
                placeholder="Search by company name"
                hasBottomContent={false}
              />
              <SelectInput
                name="isApproved"
                label="Approved"
                value={filterData.isApproved || ''}
                onChange={handleInputChange}
                options={APPROVED_OPTIONS}
                hasBottomContent={false}
              />
            </Box>
          }
          sorter={sorter}
          onSortChange={handleSortChange}
          onRowClick={(row) => {
            setEditInsuranceCompanyState(row)
            setIsCreateOpen(true)
          }}
          onPageChange={(pageNumber) => {
            searchState.setSearchState({ page: pageNumber }, false)
          }}
          pageLength={insuranceCompanies?.getInsuranceCompanies?.pagination.length}
          refetch={refetch}
        />
      </Container>
      <CreateCompanyDialog
        isOpen={isCreateOpen}
        onDismiss={() => {
          setIsCreateOpen(false)
          setEditInsuranceCompanyState(undefined)
        }}
        refetch={refetch}
        editInsuranceCompanyState={editInsuranceCompanyState}
      />
    </DashboardPage>
  )
}

export default InsuranceCompanies
